import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "@/i18n";
import { INSIGHTS } from "@/content/pages-insights";
import { Eyebrow, Reveal } from "@/components/Reveal";

const strings = {
  fr: {
    eyebrow: "Insights",
    title: "Intelligence de marché & perspectives",
    intro:
      "Recherche, analyses et perspectives stratégiques de nos conseillers — marchés immobiliers, structuration du capital et stratégie d'entreprise.",
    featured: "À la une",
    readMore: "Lire l'article",
    newsletterTitle: "Restez informé",
    newsletterText: "Analyses de marché, mises à jour stratégiques et contenus exclusifs pour investisseurs et dirigeants.",
    newsletterNote: "Abonnement sur simple demande via notre formulaire de contact.",
  },
  en: {
    eyebrow: "Insights",
    title: "Market intelligence & perspectives",
    intro:
      "Research, analysis and strategic perspectives from our advisors — real estate markets, capital structuring and corporate strategy.",
    featured: "Featured",
    readMore: "Read article",
    newsletterTitle: "Stay informed",
    newsletterText: "Market insights, strategy updates and exclusive content for forward-thinking investors and leaders.",
    newsletterNote: "Subscribe on request via our contact form.",
  },
  de: {
    eyebrow: "Insights",
    title: "Marktintelligenz & Perspektiven",
    intro:
      "Research, Analysen und strategische Perspektiven unserer Berater — Immobilienmärkte, Kapitalstrukturierung und Unternehmensstrategie.",
    featured: "Highlight",
    readMore: "Artikel lesen",
    newsletterTitle: "Bleiben Sie informiert",
    newsletterText: "Marktanalysen, Strategie-Updates und exklusive Inhalte für vorausschauende Investoren und Führungskräfte.",
    newsletterNote: "Abonnement auf Anfrage über unser Kontaktformular.",
  },
};

export default function InsightsPage() {
  const { lang } = useLang();
  const s = strings[lang];
  const [featured, ...rest] = INSIGHTS;

  return (
    <div data-testid="insights-page">
      <div className="bg-[#FBF8F3] pb-16 pt-40 sm:pt-48">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Eyebrow>{s.eyebrow}</Eyebrow>
          <h1 className="mt-6 max-w-3xl font-display text-4xl font-bold leading-[1.05] tracking-tight text-[#0A1F18] sm:text-5xl lg:text-6xl">
            {s.title}
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#5C6B64] sm:text-lg">{s.intro}</p>
        </div>
      </div>

      <section className="bg-[#F3EFE6] py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Link
              to={`/insights/${featured.slug}`}
              data-testid="insight-featured"
              className="group grid gap-px border border-[#E4DDD0] bg-[#E4DDD0] lg:grid-cols-2"
            >
              <div className="flex flex-col justify-between bg-white p-10 sm:p-14">
                <div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#B36C45]">
                    {featured.category[lang]} · {s.featured}
                  </span>
                  <h2 className="mt-6 font-display text-2xl font-semibold leading-tight tracking-tight text-[#0A1F18] sm:text-3xl">
                    {featured.title[lang]}
                  </h2>
                  <p className="mt-5 text-sm leading-relaxed text-[#5C6B64]">{featured.excerpt[lang]}</p>
                </div>
                <span className="mt-8 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#B36C45]">
                  {featured.date[lang]} · {featured.read}
                  <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </span>
              </div>
              <div className="flex min-h-[240px] items-center justify-center bg-[#C87D55] p-10">
                <span className="rotate-45 border-[16px] border-[#FBF8F3]/25 p-10">
                  <span className="block h-8 w-8 bg-[#FBF8F3]" />
                </span>
              </div>
            </Link>
          </Reveal>

          <div className="mt-px grid gap-px border-x border-b border-[#E4DDD0] bg-[#E4DDD0] md:grid-cols-2 lg:grid-cols-3">
            {rest.map((a, i) => (
              <Reveal key={a.slug} delay={i * 0.06} className="h-full">
                <Link
                  to={`/insights/${a.slug}`}
                  data-testid={`insight-card-${a.slug}`}
                  className="group flex h-full flex-col justify-between bg-white p-8 transition-colors duration-300 hover:bg-[#FBF8F3]"
                >
                  <div>
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#B36C45]">
                      {a.category[lang]}
                    </span>
                    <h3 className="mt-4 font-display text-xl font-semibold leading-snug tracking-tight text-[#0A1F18]">
                      {a.title[lang]}
                    </h3>
                    <p className="mt-4 text-sm leading-relaxed text-[#5C6B64]">{a.excerpt[lang]}</p>
                  </div>
                  <span className="mt-6 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#B36C45]">
                    {a.date[lang]} · {a.read}
                    <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#C87D55] py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-center">
            <div>
              <h2 className="font-display text-2xl font-semibold text-[#FBF8F3] sm:text-3xl">{s.newsletterTitle}</h2>
              <p className="mt-3 max-w-xl text-sm text-[#FBF8F3]/85">
                {s.newsletterText} {s.newsletterNote}
              </p>
            </div>
            <Link
              to="/contact"
              data-testid="insights-newsletter-cta"
              className="shrink-0 bg-[#0A1F18] px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-[#FBF8F3] transition-colors hover:bg-[#153328]"
            >
              Contact
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
